import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import useMediaQuery from '@mui/material/useMediaQuery';
import useProfileStore from '@store/profile-store/profile.store.ts';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import useServicesStore from '@modules/services/store/services.store.ts';
import { ServicesModel } from '@models/services.model.ts';
import { servicesColumns } from '@modules/services/components/services-table/components/columns/services-columns.tsx';
import { getServices } from '@api/services-api.ts';
import ServicesDialogContent from '@modules/services/components/services-dialog/services-dialog-content.tsx';
import { RoutePaths } from '@utils/route.utils.ts';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';

const ServicesTable: React.FC = () => {
  const {
    getDepartmentUnit,
    createService,
    editService,
    deleteService,
    togglePublish,
    departmentUnit,
    isLoading
  } = useServicesStore();
  const { selectedCurrency } = useProfileStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [updateService, setUpdateService] = useState<ServicesModel | null>(
    null
  );
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [searchParams, setSearchParams] = useSearchParams();
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const location = useLocation();
  const isServicesPage =
    location.pathname === RoutePaths.Core.Services.absolutePath;

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      await getDepartmentUnit();

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        handleOpen();
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  const handleEdit = (row: ServicesModel) => {
    setUpdateService(row);
    setOpenDialog(true);
  };

  const handleDelete = (row: ServicesModel) => {
    setUpdateService(row);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteService({
      id: updateService?.id,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleClose = () => {
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setUpdateService(null);
  };

  const handleOpen = () => setOpenDialog(true);

  const handleFormSave = (data: any) => {
    if (updateService?.id) {
      editService({
        data,
        id: updateService?.id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createService({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    if (action.type === ViewControlsEnum.Language) {
      setLanguage(action.value as LanguageEnum);
    }
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      handleOpen();
    }
  };

  const handlePublish = (row: ServicesModel) => {
    togglePublish({
      id: row?.id,
      state: tableStore.getState(),
      handleClose
    });
  };

  const columns = servicesColumns(
    handlePublish,
    handleEdit,
    handleDelete,
    selectedCurrency,
    language,
    departmentUnit,
    t
  );

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls onAction={handleAction} />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={handleOpen}
        >
          {t('add')}
        </Button>
      </div>

      <div
        className={
          isServicesPage
            ? TABLE_HEIGHT.WITHOUT_TABS
            : TABLE_HEIGHT.WITH_TABS_AND_ACTIONS
        }
      >
        <DynamicTable
          tableStore={tableStore}
          apiFunction={getServices}
          onTriggerAction={handleTableTrigger}
          columns={columns}
          language={language}
          tableKey="services-table"
          initialState={{
            sort: ['createdAt', SortOrder.Descending]
          }}
        />
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          size={isTabletScreen ? 'md' : 'lg'}
          title={t('services_dialog_title')}
          content={
            <ServicesDialogContent
              departmentUnit={departmentUnit}
              updateServiceData={updateService}
              onSave={handleFormSave}
            />
          }
          onClose={handleClose}
        />

        <div className="w-full h-full">
          <DeleteDialog
            onCancel={handleClose}
            onDelete={handleDeleteConfirm}
            open={showDeleteDialog}
            isLoading={isLoading()}
            title={t('delete_popup_title')}
          />
        </div>
      </div>
    </div>
  );
};

export default ServicesTable;
